import { Fragment, createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import api from '../utils/api';

const AccessContext = createContext({ permissions: {}, is_owner: false });
export const usePortalAccess = () => useContext(AccessContext);
export const AccessSnapshot = AccessContext.Provider;

export const PORTAL_SECTIONS = [
  { id: 'helpdesk', label: '📋 Helpdesk', items: [
    ['helpdesk.dashboard', '/admin/dashboard', '📊 Статистика'],
    ['helpdesk.tickets', '/admin/tickets', '📑 Заявки'],
    ['helpdesk.users', '/admin/users', '👤 Пользователи'],
    ['helpdesk.networks', '/admin/settings/networks', '⚙️ Сети и точки'],
  ] },
  { id: 'knowledge', label: '📚 База знаний', items: [['knowledge.manage', '/admin/knowledge', '📖 Материалы']] },
  { id: 'inventory', label: '🖥 Оборудование', items: [['inventory.manage', '/admin/inventory', '📋 Реестр техники']] },
  { id: 'signage', label: '📺 TV-экраны', items: [['signage.manage', '/admin/signage', '🧩 Signage']] },
  { id: 'smm', label: '📈 SMM Dash', items: [
    ['smm.overview', '/admin/smm/dashboard', '🏠 Обзор'], ['smm.vk', '/admin/smm/vk', '💙 ВКонтакте'],
    ['smm.telegram', '/admin/smm/telegram', '✈️ Telegram'], ['smm.instagram', '/admin/smm/instagram', '📸 Instagram'],
    ['smm.max', '/admin/smm/max', '🟠 Макс'], ['smm.settings', '/admin/smm/settings', '⚙️ Настройки'],
  ] },
  { id: 'content_plan', label: '🗓 Контент-план', items: [['content_plan.manage', '/admin/content-plan', '📅 Планирование']] },
  { id: 'academy', label: '🎓 Академия', items: [
    ['academy.learn', '/admin/academy?mode=learn', '📖 Обучение'],
    ['academy.edit', '/admin/academy?mode=manage', '✏️ Редактор обучения'],
  ] },
];

export function firstPortalPath(permissions) {
  return PORTAL_SECTIONS.flatMap(s => s.items).find(([key]) => permissions[key])?.[1] || '/app';
}

export function PortalIndex() {
  const { permissions } = usePortalAccess();
  return <Navigate to={firstPortalPath(permissions)} replace />;
}

export function SmmIndex() {
  const { permissions } = usePortalAccess();
  const pages = [['smm.overview', 'dashboard'], ['smm.vk', 'vk'], ['smm.telegram', 'telegram'], ['smm.instagram', 'instagram'], ['smm.max', 'max']];
  const page = pages.find(([key]) => permissions[key]);
  return page ? <Navigate to={`/smm/${page[1]}`} replace /> : <AccessDenied />;
}

export function AccessDenied({ home = '/app' }) {
  return <div className="page"><h1>Нет доступа к разделу</h1><p>Администратор может открыть его в настройках доступа.</p><Link to={home}>Вернуться к доступным разделам</Link></div>;
}

export function OwnerGuard({ children }) {
  const { is_owner: isOwner } = usePortalAccess();
  return isOwner ? children : <AccessDenied />;
}

export function SectionGuard({ permission, children }) {
  const { permissions } = usePortalAccess();
  return permissions[permission] ? children : <AccessDenied />;
}

export function PortalAccessProvider({ children, standalone = false }) {
  const [access, setAccess] = useState(null);
  const fingerprint = useRef('');
  const [error, setError] = useState('');
  const location = useLocation();
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('portal_token') || (standalone && localStorage.getItem('smm_token'));
      if (!token) { if (active) setError('auth'); return; }
      try {
        const response = await api.get('/api/access/me', { headers: { Authorization: `Bearer ${token}` } });
        if (active) {
          const next = JSON.stringify([response.data.user_id, response.data.permissions]);
          if (fingerprint.current && fingerprint.current !== next) window.dispatchEvent(new Event('portal-access-changed'));
          fingerprint.current = next;
          setAccess(response.data); setError('');
        }
      } catch (err) {
        if (active) { setAccess(null); setError([401, 403].includes(err.response?.status) ? 'auth' : 'connection'); }
      }
    };
    void refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [location.pathname, standalone]);
  if (error === 'auth') return <Navigate to={standalone ? '/smm/login' : '/admin/login'} replace />;
  if (error) return <div className="page"><p role="alert">Не удалось проверить доступ.</p><button className="btn-secondary" onClick={() => window.location.reload()}>Повторить</button></div>;
  if (!access) return <div className="page-loader">Проверка доступа…</div>;
  return <AccessSnapshot value={access}><Fragment key={fingerprint.current}>{children}</Fragment></AccessSnapshot>;
}
